
import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import StatCards from './StatCards';
import ConsumptionBarChart from './ConsumptionBarChart';
import EnergyDistributionPieChart from './EnergyDistributionPieChart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BrainCircuit, Lightbulb, TrendingDown, AlertCircle, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { aiPredictions, maintenanceAlerts } from '@/components/dashboard/maintenance/data/maintenanceData';

interface AIAdvisoryTabProps {
  initialSubTab?: string;
}

const AIAdvisoryTab = ({ initialSubTab }: AIAdvisoryTabProps = {}) => {
  const [subTab, setSubTab] = useState(initialSubTab || 'advisories');
  
  useEffect(() => {
    if (initialSubTab) {
      setSubTab(initialSubTab);
    }
  }, [initialSubTab]);
  
  const advisories = [
    {
      id: 1,
      title: "Energy Usage Optimization",
      description: "Based on consumption patterns, reducing HVAC usage during 2-4PM could save 15% on monthly energy costs.",
      impact: "High",
      category: "Efficiency"
    },
    {
      id: 2,
      title: "Maintenance Alert",
      description: "Smart meter SM-102 showing unusual voltage fluctuations. Preventative maintenance recommended within 14 days.",
      impact: "Medium",
      category: "Maintenance"
    },
    {
      id: 3,
      title: "Peak Demand Prediction",
      description: "Tomorrow's weather forecast indicates high temperature. Expected 20% increase in energy demand between 1-5PM.",
      impact: "High",
      category: "Planning"
    },
    {
      id: 4,
      title: "Device Replacement",
      description: "Gateway GW-05 efficiency has declined 12% in the last month. Consider replacement within next quarter.",
      impact: "Low",
      category: "Equipment"
    }
  ];

  // Get maintenance alerts and filter to show only critical ones for this summary
  const maintenanceItems = maintenanceAlerts
    .filter(alert => alert.status === 'Alert' || alert.status === 'Warning')
    .slice(0, 3);
  
  // Get AI predictions for maintenance
  const maintenancePredictions = aiPredictions.slice(0, 3);
  
  return (
    <div className="h-full">
      <Tabs value={subTab} onValueChange={setSubTab} className="h-full flex flex-col">
        <div className="flex-none">
          <TabsList className="w-full grid grid-cols-4">
            <TabsTrigger value="advisories" className="text-xs py-1">AI Advisories</TabsTrigger>
            <TabsTrigger value="maintenance" className="text-xs py-1">Maintenance AI</TabsTrigger>
            <TabsTrigger value="stats" className="text-xs py-1">Stats</TabsTrigger>
            <TabsTrigger value="charts" className="text-xs py-1">Charts</TabsTrigger>
          </TabsList>
        </div>
        
        <div className="flex-1 overflow-hidden mt-2">
          <TabsContent value="advisories" className="h-full overflow-auto m-0">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <BrainCircuit className="h-5 w-5 text-blue-500" />
                  AI-Powered Recommendations
                </h2>
                <Button variant="outline" size="sm">Export Insights</Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {advisories.map(advisory => (
                  <Card key={advisory.id} className="border-l-4 border-l-blue-500">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg font-medium flex items-center gap-2">
                          {advisory.category === "Efficiency" && <Lightbulb className="h-4 w-4 text-amber-500" />}
                          {advisory.category === "Maintenance" && <AlertCircle className="h-4 w-4 text-red-500" />}
                          {advisory.category === "Planning" && <TrendingDown className="h-4 w-4 text-blue-500" />}
                          {advisory.category === "Equipment" && <AlertCircle className="h-4 w-4 text-orange-500" />}
                          {advisory.title}
                        </CardTitle>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          advisory.impact === "High" ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" : 
                          advisory.impact === "Medium" ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" : 
                          "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                        }`}>
                          {advisory.impact} Impact
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{advisory.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="maintenance" className="h-full overflow-auto m-0">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-blue-500" />
                  Preventive & Predictive Maintenance
                </h2>
                <Button variant="outline" size="sm">View Full Maintenance Dashboard</Button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    Critical Maintenance Alerts
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {maintenanceItems.map((item, index) => (
                      <Card key={index} className="border-l-4 border-l-red-500">
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-md font-medium">{item.title}</CardTitle>
                            <div className={`p-1.5 rounded-full ${item.bgClass}`}>
                              {item.icon}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{item.issue}</p>
                          {item.aiPrediction && (
                            <p className="text-xs bg-blue-50 dark:bg-blue-900/20 p-2 mt-2 rounded">
                              <span className="font-medium">AI Prediction:</span> {item.aiPrediction}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                    <BrainCircuit className="h-4 w-4 text-blue-500" />
                    AI Maintenance Predictions
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {maintenancePredictions.map((prediction, index) => (
                      <Card key={index} className="border-l-4 border-l-blue-500">
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-md font-medium">{prediction.title}</CardTitle>
                            <div className="bg-blue-100 dark:bg-blue-900/30 p-1.5 rounded-full">
                              {prediction.icon}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{prediction.description}</p>
                          {prediction.recommendedAction && (
                            <p className="text-xs bg-green-50 dark:bg-green-900/20 p-2 mt-2 rounded">
                              <span className="font-medium">Recommendation:</span> {prediction.recommendedAction}
                            </p>
                          )}
                          <div className="mt-2 flex justify-between">
                            <span className="text-xs text-gray-600">Accuracy: {prediction.accuracy}</span>
                            <span className="text-xs text-blue-600">Impact: {prediction.impact}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="stats" className="h-full overflow-auto m-0">
            <StatCards />
          </TabsContent>
          
          <TabsContent value="charts" className="h-full overflow-auto m-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <ConsumptionBarChart />
              <EnergyDistributionPieChart />
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default AIAdvisoryTab;
